import React, { useState, useEffect } from 'react';
import ReportStatusBadge from '../../../../utils/ReportStatusBadge';
import { Link } from 'react-router-dom';
import { Mercato, TipoMercatoKey, Venue } from '../../../../api/apiTypes';
import Paginator from '../../../../utils/Paginator';
import { routesMap } from '../../../../routes/AppRoutes';
import ListaMercatiModale from './adesione-mercati-dettaglio/ListaMercatiModale';
import { Eye } from 'lucide-react';

type ContainerListaDriverPageProps = {
  intermediari: Mercato[];
  driverRoute: string | undefined;
  reportId: string;
  id: string;
  driverId: string;
};

const tipiMercati = [
  { key: 'equity', label: 'Azioni' },
  { key: 'bonds', label: 'Obbligazioni' },
  { key: 'derivatives', label: 'Derivati' },
  { key: 'others', label: 'Altri mercati' },
] as const;

const ContainerAdesioneMercati = ({ reportId, intermediari, driverRoute, id, driverId }: ContainerListaDriverPageProps) => {
  const itemsPerPage = 6;

  // 🔹 Recupera la pagina salvata (se esiste)
  const storedPage = Number(sessionStorage.getItem('adesioneMercatiCurrentPage')) || 1;
  const [currentPage, setCurrentPage] = useState(storedPage);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<{
    tipoMercato: string;
    lista: Venue[];
    intermediarioNome: string;
    isAmbiguous?: boolean;
  } | null>(null);

  const getFilteredVenues = (intermediario: Mercato, tipoMercatoKey: TipoMercatoKey): Venue[] => {
    const mercato = intermediario?.value?.[tipoMercatoKey];
    if (!mercato || !mercato.venues) return [];
    return mercato.venues;
  };

  const getAllFilteredVenues = (intermediario: Mercato): Venue[] => {
    return tipiMercati.flatMap(({ key }) => getFilteredVenues(intermediario, key));
  };

  const totalPages = Math.max(1, Math.ceil(intermediari.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // 🔹 Salva la pagina corrente ogni volta che cambia
  useEffect(() => {
    sessionStorage.setItem('adesioneMercatiCurrentPage', currentPage.toString());
  }, [currentPage]);

  const paginatedIntermediari = intermediari.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleOpenModal = (tipoMercato: string, lista: Venue[], intermediarioNome: string) => {
    setModalData({ tipoMercato, lista, intermediarioNome });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalData(null);
  };

  return (
    <div className="">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">

        {/* Header tabella */}
        <div className="grid grid-cols-[2fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_2fr_0.8fr_0.8fr] gap-x-2 p-6 border-b border-gray-100 bg-gray-50 font-semibold text-[#1B365D] text-xs">
          <div className="flex items-center">Identificativo</div>
          {tipiMercati.map(({ label }) => (
            <div key={label} className="flex justify-center items-center">{label}</div>
          ))}
          <div className="flex justify-center items-center">Totale mercati</div>
          <div className="flex justify-center items-center">Score</div>
          <div className="flex justify-center items-center">Stato</div>
          <div className="flex justify-center items-center">Validazione</div>
          <div />
        </div>

        {/* Lista intermediari */}
        <div className="divide-y divide-gray-100">
          {paginatedIntermediari.map((intermediario) => {
            const totaleMercati = tipiMercati.reduce((sum, { key }) => sum + getFilteredVenues(intermediario, key).length, 0);

            return (
              <div
                key={intermediario.id}
                className="grid grid-cols-[2fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_2fr_0.8fr_0.8fr] gap-x-2 p-4 hover:bg-gray-50/50 transition duration-150 ease-in-out items-center text-xs"
              >
                <div className="flex items-center text-gray-800 ml-2">{intermediario.name}</div>

                {tipiMercati.map(({ key }) => {
                  const filteredVenues = getFilteredVenues(intermediario, key);
                  return (
                    <div
                      key={key}
                      className="flex justify-center items-center text-primary font-semibold cursor-pointer hover:underline"
                      onClick={() => handleOpenModal(tipiMercati.find(m => m.key === key)?.label ?? '', filteredVenues, intermediario.name)}
                    >
                      {filteredVenues.length}
                    </div>
                  );
                })}

                <div
                  className="flex justify-center items-center text-primary font-semibold cursor-pointer hover:underline"
                  onClick={() => handleOpenModal("Totale mercati", getAllFilteredVenues(intermediario), intermediario.name)}
                >
                  {totaleMercati}
                </div>

                <div className="flex justify-center items-center">
                  {intermediario.value?.score?.total_score}
                </div>

                <div className="flex justify-center items-center">
                  <ReportStatusBadge stato={intermediario.status} />
                </div>

                <div className="flex justify-center items-center text-center">
                  <ReportStatusBadge stato={intermediario.validation} />
                </div>

                <div className="flex justify-end">
                  <Link
                    to={routesMap.VISUALIZZA_INTERMEDIARIO(reportId, driverRoute ?? '', intermediario.name)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#1B365D] text-white hover:bg-[#142a4a] transition-colors"
                    state={{ id, driverId, key: intermediario.id }}
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Modale dettagli mercati */}
        {isModalOpen && modalData && (
          <ListaMercatiModale
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            title={`Dettagli ${modalData.tipoMercato} – ${modalData.intermediarioNome}`}
            lista={modalData.lista}
            isEditing={false}
            isAmbiguousList={false}
            onAdd={() => {}}
            onRemove={() => {}}
          />
        )}

        {/* Paginazione */}
        {totalPages > 0 && (
          <div className="p-4">
            <Paginator
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ContainerAdesioneMercati;
